import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MessageSquare, Gavel, TrendingDown, Info, CheckCheck, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import { toast } from "sonner";
import { emitNotifRefresh } from "../components/NotificationBell";

const TYPE_META = {
    inquiry:    { icon: MessageSquare,  color: "text-blue-600",    bg: "bg-blue-500/10",    label: "Inquiry" },
    auction:    { icon: Gavel,          color: "text-orange-600",  bg: "bg-orange-500/10",  label: "Auction" },
    price_drop: { icon: TrendingDown,   color: "text-emerald-600", bg: "bg-emerald-500/10", label: "Price Drop" },
    system:     { icon: Info,           color: "text-navy-500",    bg: "bg-navy-500/10",    label: "System" },
};

const FILTERS = [
    { key: "all",        label: "All" },
    { key: "unread",     label: "Unread" },
    { key: "inquiry",    label: "Inquiries" },
    { key: "auction",    label: "Auctions" },
    { key: "price_drop", label: "Price drops" },
];

const timeAgo = (iso) => {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
};

export default function Notifications() {
    const { user, loading: authLoading } = useAuth();
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (authLoading) return;
        if (!user) { nav("/login?next=/notifications"); }
    }, [user, authLoading, nav]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/notifications?limit=200");
            setItems(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error("Failed to load notifications");
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { if (user) load(); }, [user, load]);

    const markRead = async (n) => {
        try {
            await api.patch(`/notifications/${n.id}/read`);
            setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x));
            emitNotifRefresh();
        } catch { toast.error("Could not mark as read"); }
    };

    const remove = async (n) => {
        try {
            await api.delete(`/notifications/${n.id}`);
            setItems((prev) => prev.filter((x) => x.id !== n.id));
            emitNotifRefresh();
            toast.success("Notification removed");
        } catch { toast.error("Could not remove notification"); }
    };

    const markAllRead = async () => {
        try {
            await api.post("/notifications/read-all");
            setItems((prev) => prev.map((x) => ({ ...x, read: true })));
            emitNotifRefresh();
            toast.success("All notifications marked as read");
        } catch { toast.error("Failed to mark all read"); }
    };

    const filtered = items.filter((n) => {
        if (filter === "all") return true;
        if (filter === "unread") return !n.read;
        return n.type === filter;
    });

    const onOpen = (n) => {
        if (!n.read) markRead(n);
        if (n.link) nav(n.link);
    };

    if (!user) return null;

    return (
        <div className="min-h-screen bg-cream-50">
            <Seo title="Notifications — Global Property Mart" description="Your inquiry, auction and price-drop alerts, all in one place." path="/notifications" />

            <div className="max-w-4xl mx-auto px-5 lg:px-8 py-8 lg:py-12">
                <div className="flex items-start justify-between gap-4 mb-6">
                    <div>
                        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-navy-500 flex items-center gap-3">
                            <Bell className="w-7 h-7 text-gold-500" />
                            Notifications
                        </h1>
                        <p className="text-navy-500/60 text-sm mt-1">
                            Inquiries, auction alerts and price drops on your saved properties.
                        </p>
                    </div>
                    <button
                        onClick={markAllRead}
                        data-testid="notif-page-mark-all"
                        className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-gold-500/40 text-gold-700 text-xs font-bold uppercase tracking-wider hover:bg-gold-500/10"
                    >
                        <CheckCheck className="w-3.5 h-3.5" /> Mark all read
                    </button>
                </div>

                {/* Filter pills */}
                <div className="flex gap-2 overflow-x-auto pb-3 mb-4" data-testid="notif-filters">
                    {FILTERS.map((f) => (
                        <button
                            key={f.key}
                            onClick={() => setFilter(f.key)}
                            data-testid={`notif-filter-${f.key}`}
                            className={`whitespace-nowrap px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border transition-all ${
                                filter === f.key
                                    ? "bg-navy-500 text-white border-navy-500"
                                    : "bg-white text-navy-500 border-navy-500/15 hover:border-gold-500/50 hover:text-gold-700"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>

                <div className="bg-white rounded-xl border border-navy-500/10 divide-y divide-navy-500/5" data-testid="notif-page-list">
                    {loading && (
                        <div className="px-6 py-14 text-center text-navy-500/60">Loading…</div>
                    )}
                    {!loading && filtered.length === 0 && (
                        <div className="px-6 py-14 text-center">
                            <Bell className="w-10 h-10 mx-auto text-navy-500/15 mb-3" />
                            <div className="font-heading text-lg font-bold text-navy-500">No notifications</div>
                            <div className="text-sm text-navy-500/60 mt-1">
                                {filter === "all"
                                    ? "New inquiries, price drops and auction alerts will appear here."
                                    : "No notifications match this filter."}
                            </div>
                        </div>
                    )}
                    {!loading && filtered.map((n) => {
                        const meta = TYPE_META[n.type] || TYPE_META.system;
                        const Icon = meta.icon;
                        return (
                            <div
                                key={n.id}
                                data-testid={`notif-row-${n.id}`}
                                className={`group flex items-start gap-4 px-4 sm:px-6 py-4 hover:bg-gold-500/5 transition-colors ${n.read ? "opacity-75" : "bg-gold-500/[0.03]"}`}
                            >
                                <div className={`shrink-0 w-11 h-11 rounded-full ${meta.bg} flex items-center justify-center`}>
                                    <Icon className={`w-5 h-5 ${meta.color}`} />
                                </div>
                                <button
                                    onClick={() => onOpen(n)}
                                    className="flex-1 min-w-0 text-left"
                                >
                                    <div className="flex items-center gap-2">
                                        <span className={`text-[10px] font-bold uppercase tracking-widest ${meta.color}`}>
                                            {meta.label}
                                        </span>
                                        {!n.read && <span className="w-2 h-2 rounded-full bg-gold-500" />}
                                        <span className="text-[10px] text-navy-500/40 uppercase tracking-wider ml-auto">
                                            {timeAgo(n.created_at)}
                                        </span>
                                    </div>
                                    <div className={`mt-0.5 ${n.read ? "font-medium" : "font-bold"} text-navy-500`}>
                                        {n.title}
                                    </div>
                                    {n.body && (
                                        <div className="text-sm text-navy-500/70 mt-0.5 line-clamp-2">{n.body}</div>
                                    )}
                                </button>
                                <button
                                    onClick={() => remove(n)}
                                    aria-label="Delete notification"
                                    data-testid={`notif-delete-${n.id}`}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity p-2 text-navy-500/40 hover:text-red-500"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        );
                    })}
                </div>

                <div className="sm:hidden mt-4">
                    <button
                        onClick={markAllRead}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md border border-gold-500/40 text-gold-700 text-sm font-bold uppercase tracking-wider hover:bg-gold-500/10"
                    >
                        <CheckCheck className="w-4 h-4" /> Mark all read
                    </button>
                </div>
            </div>
        </div>
    );
}
